import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Plus, Trash2 } from "react-feather";
import { useMasterPermissions } from "../useMasterPermissions";
import { useGetTaxSlabsQuery, useSaveTaxSlabMutation, useDeleteTaxSlabMutation } from "./taxApi";

const emptyRow = () => ({ slNo: null, taxFormulaAmount: "", taxAmount: "", deductionPercentage: "" });

const recalcCumulative = (rows) =>
  rows.map((row, index) => {
    const formula = Number(row.taxFormulaAmount) || 0;
    const prevAmount = index === 0 ? 0 : Number(rows[index - 1].taxAmount) || 0;
    return { ...row, taxAmount: String(index === 0 ? formula : prevAmount + formula) };
  });

// Mirrors GUI/Master/frm_tax.cs ("Payee"), now against the live
// /api/v1/masters/tax endpoints (handoff §9.5). Not a normal master - one
// fixed config with N slab rows, upserted one at a time by slNo (null =
// new row). dedAmount is computed server-side; taxAmount (the cumulative
// running total) is still computed client-side, same as the legacy grid.
// Reading requires Tax.View (route-gated); Tax has no .Create at all (§13.3)
// - every write, including the very first slab, is Tax.Edit + all-branch.
const TaxPage = () => {
  const { canEdit: canManage } = useMasterPermissions("Tax");
  const { data: slabs = [], isLoading } = useGetTaxSlabsQuery();
  const [saveTaxSlab, { isLoading: isSaving }] = useSaveTaxSlabMutation();
  const [deleteTaxSlab] = useDeleteTaxSlabMutation();

  const [rows, setRows] = useState([]);
  const [reliefAmount, setReliefAmount] = useState("");
  const [pendingDeletes, setPendingDeletes] = useState([]);
  const [isReadOnly, setIsReadOnly] = useState(true);

  const syncFromServer = () => {
    if (slabs.length > 0) {
      const sorted = [...slabs].sort((a, b) => a.slNo - b.slNo);
      setRows(
        sorted.map((s) => ({
          slNo: s.slNo,
          taxFormulaAmount: String(s.taxFormulaAmount),
          taxAmount: String(s.taxAmount),
          deductionPercentage: String(s.deductionPercentage),
        }))
      );
      setReliefAmount(String(sorted[0].reliefAmount ?? ""));
      setIsReadOnly(true);
    } else {
      setRows([emptyRow()]);
      setReliefAmount("");
      setIsReadOnly(false);
    }
    setPendingDeletes([]);
  };

  useEffect(() => {
    if (!isLoading) syncFromServer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, slabs]);

  const editable = canManage && !isReadOnly;

  const updateFormulaAmount = (index, value) => {
    setRows((prev) => {
      const next = prev.map((r, i) => (i === index ? { ...r, taxFormulaAmount: value } : r));
      return recalcCumulative(next);
    });
  };

  const updatePercentage = (index, value) => {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, deductionPercentage: value } : r)));
  };

  const addRow = () => setRows((prev) => [...prev, emptyRow()]);

  const removeRow = (index) => {
    setRows((prev) => {
      const row = prev[index];
      if (row.slNo != null) setPendingDeletes((d) => [...d, row.slNo]);
      return recalcCumulative(prev.filter((_, i) => i !== index));
    });
  };

  const handleSave = async () => {
    const invalid = rows.some(
      (r) =>
        r.taxFormulaAmount === "" ||
        r.deductionPercentage === "" ||
        Number.isNaN(Number(r.taxFormulaAmount)) ||
        Number.isNaN(Number(r.deductionPercentage))
    );
    if (invalid) {
      toast.error("Every band needs a numeric formula amount and percentage");
      return;
    }
    try {
      for (const slNo of pendingDeletes) {
        // eslint-disable-next-line no-await-in-loop
        await deleteTaxSlab(slNo).unwrap();
      }
      for (const row of rows) {
        // eslint-disable-next-line no-await-in-loop
        await saveTaxSlab({
          slNo: row.slNo,
          taxFormulaAmount: Number(row.taxFormulaAmount),
          taxAmount: Number(row.taxAmount),
          deductionPercentage: Number(row.deductionPercentage),
          reliefAmount: Number(reliefAmount) || 0,
        }).unwrap();
      }
      toast.success("Tax bands saved");
    } catch (error) {
      toast.error(error?.message || "Could not save tax bands");
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Payee (Tax)</h4>
              <h6>PAYE tax bands and personal relief</h6>
            </div>
          </div>
        </div>

        {!canManage && (
          <div className="alert alert-info">
            You have read-only access. Managing tax bands requires Tax.Edit and an all-branch
            administrator.
          </div>
        )}

        <div className="card mb-4">
          <div className="card-body">
            <h5 className="mb-3">Tax Details</h5>

            <div className="table-responsive mb-3">
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: 70 }}>Sl No</th>
                    <th>Formula Amount</th>
                    <th>Calculation Amount</th>
                    <th>Percentage</th>
                    <th>Deduction Amount</th>
                    {editable && <th style={{ width: 40 }} />}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    <tr key={row.slNo ?? `new-${index}`}>
                      <td>{row.slNo ?? "New"}</td>
                      <td>
                        <input
                          type="number"
                          className="form-control"
                          value={row.taxFormulaAmount}
                          onChange={(e) => updateFormulaAmount(index, e.target.value)}
                          readOnly={!editable}
                          disabled={!editable}
                        />
                      </td>
                      <td>
                        <input type="text" className="form-control" value={row.taxAmount} readOnly disabled />
                      </td>
                      <td>
                        <input
                          type="number"
                          className="form-control"
                          value={row.deductionPercentage}
                          onChange={(e) => updatePercentage(index, e.target.value)}
                          readOnly={!editable}
                          disabled={!editable}
                        />
                      </td>
                      <td>
                        {(
                          (Number(row.taxAmount) || 0) * (Number(row.deductionPercentage) || 0) / 100
                        ).toFixed(2)}
                      </td>
                      {editable && (
                        <td>
                          <button
                            type="button"
                            className="btn btn-link p-0 text-danger"
                            onClick={() => removeRow(index)}
                            disabled={rows.length === 1}
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {editable && (
              <button type="button" className="btn btn-outline-primary btn-sm mb-3" onClick={addRow}>
                <Plus size={14} className="me-1" />
                Add Band
              </button>
            )}

            <div className="row">
              <div className="col-md-4 mb-3">
                <label className="form-label">Relief Amount</label>
                <input
                  type="number"
                  className="form-control"
                  value={reliefAmount}
                  onChange={(e) => setReliefAmount(e.target.value)}
                  readOnly={!editable}
                  disabled={!editable}
                />
              </div>
            </div>

            {canManage && (
              <div className="d-flex gap-2">
                {!isReadOnly && (
                  <button type="button" className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
                    Save
                  </button>
                )}
                {isReadOnly && (
                  <button type="button" className="btn btn-outline-primary" onClick={() => setIsReadOnly(false)}>
                    Edit
                  </button>
                )}
                <button type="button" className="btn btn-secondary" onClick={syncFromServer}>
                  Clear
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaxPage;
